// row 118 VersionTags: a strip of version tags (v1 · v2 · v3); the current one sits on --t-blue and carries
// aria-current. A tag that opens its version is a link or a button (F). row 119 Versions: dated version rows — the
// number in mono, the date, the actor and the reason (--text-2) — newest first, the newest tagged *Current* (pending,
// Q-E16). A row that opens its version is one link (F row), composed from core's Row. `pinned` holds the requisition's
// *Original posting type* above the list (B2-7): a KeyValue row with the lock (it never changes with a new version) and,
// when given, where it was set.

import type { ReactNode } from "react";
import { KeyValue } from "../core/KeyValue";
import { Avatar } from "../core/Person";
import { Row } from "../core/Row";
import { Tag } from "../core/Tag";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { DEFAULT_TIME_ZONE, formatDate, toEpoch, type Instant } from "../lib/time";
import { VERSION_LABELS, VERSION_TEMPLATES } from "./labels";

export interface VersionTag {
  n: number;
  href?: string | undefined;
  onOpen?: (() => void) | undefined;
  hs?: Hs | undefined;
}

export interface VersionTagsProps {
  versions: readonly VersionTag[];
  /** The version shown now. */
  current: number;
  /** The strip's name. */
  label: string;
  className?: string | undefined;
}

function VTag({ version, on }: { version: VersionTag; on: boolean }) {
  const hot = useHs(version.hs);
  const text = VERSION_TEMPLATES.version({ n: version.n });
  const classes = cx("mc-tag", "mc-vtag", on && "mc-vtag-on", (version.href !== undefined || version.onOpen) && "mc-vtag-link");
  const current = on ? ("true" as const) : undefined;
  if (version.href !== undefined) return <a className={classes} href={version.href} onClick={version.onOpen} aria-current={current} {...hot}>{text}</a>;
  if (version.onOpen) return <button type="button" className={classes} onClick={version.onOpen} aria-current={current} {...hot}>{text}</button>;
  return <span className={classes} aria-current={current}>{text}</span>;
}

export function VersionTags({ versions, current, label, className }: VersionTagsProps) {
  return (
    <ul className={cx("mc-vtags", className)} aria-label={label}>
      {versions.map((version) => <li key={version.n}><VTag version={version} on={version.n === current} /></li>)}
    </ul>
  );
}

export interface VersionRow {
  n: number;
  at: Instant;
  actor: string;
  actorKey?: string | undefined;
  reason: ReactNode;
  href?: string | undefined;
  onOpen?: (() => void) | undefined;
  hs?: Hs | undefined;
}

export interface VersionsProps {
  /** Any order: drawn newest first. */
  rows: readonly VersionRow[];
  /** The list's name. */
  label: string;
  /** The pinned *Original posting type* over the list: its value and, optionally, where it was set (by the lock). */
  pinned?: { value: ReactNode; from?: ReactNode | undefined } | undefined;
  timeZone?: string | undefined;
  className?: string | undefined;
}

export function Versions({ rows, label, pinned, timeZone = DEFAULT_TIME_ZONE, className }: VersionsProps) {
  const sorted = [...rows].sort((a, b) => b.n - a.n);
  const newest = sorted[0]?.n;
  const list = (
    <ul className={cx("mc-versions", !pinned && className)} aria-label={label}>
      {sorted.map((row) => (
        <Row key={row.n} as="li" cols={1} href={row.href} onOpen={row.onOpen} hs={row.hs} className="mc-version-row">
          <span className="mc-version-in">
            <span className="mc-version-n">
              <span className="mc-mono">{VERSION_TEMPLATES.version({ n: row.n })}</span>
              {row.n === newest ? <Tag className="mc-vtag-on">{VERSION_LABELS.current}</Tag> : null}
            </span>
            <time dateTime={new Date(toEpoch(row.at)).toISOString()}>{formatDate(row.at, timeZone)}</time>
            <span className="mc-version-a">
              <Avatar name={row.actor} hueKey={row.actorKey} size={24} decorative />
              {row.actor}
            </span>
            <span className="mc-version-r">{row.reason}</span>
          </span>
        </Row>
      ))}
    </ul>
  );
  if (!pinned) return list;
  return (
    <div className={cx("mc-versions-w", className)}>
      <KeyValue rows className="mc-version-pin" items={[[VERSION_LABELS.originalPostingTypeField, pinned.value, pinned.from ?? ""]]} />
      {list}
    </div>
  );
}
